import {AppState} from '@/store/state';
import {Component, HostBinding, OnInit} from '@angular/core';
import {Store} from '@ngrx/store';
import {AppService} from '@services/app.service';
import {TaskService} from '@services/task.service';
import {ToastrService} from 'ngx-toastr';
import {Observable} from 'rxjs';
import {UiState} from '@/store/ui/state';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ToggleTaskSidebar} from '@/store/ui/actions';
import {TaskPriority} from '@/common/Enum/TaskPriority.Enum';
import {AddTaskComponent} from '@/components/task/add-task/add-task.component';
import {DialogModule} from 'primeng/dialog';

interface SubTask {
    id: number;
    title: string;
    createdBy: string;
    assignedTo: string;
    dueDate: string;
    startDate: string;
    status: string;
    done: boolean;
    priority?: TaskPriority;
    description?: string;
    category?: string;
    farmId?: number;
    farmName?: string;
    houseId?: number;
    houseName?: string;
    partitionId?: number;
    partitionName?: string;
    cycleId?: number;
    cycleName?: string;
    createdDate?: string;
    updatedDate?: string;
    attachments?: TaskAttachment[];
    subTasks?: [];
}

interface TaskAttachment {
    id: number;
    fileName: string;
    fileType: string;
    fileSize: number;
    uploadDate: string;
    uploadedBy: string;
}

interface Task {
    id: number;
    title: string;
    assignedTo: string; // User from dropdown list
    startDate: string;
    dueDate: string;
    subTasks: SubTask[];
    createdBy: string; // System or Username "Manual"
    status: string;
    statusId?: number;
    attachments: TaskAttachment[];
    category: string;
    // Required farm/location fields
    farmId: number;
    farmName: string;
    houseId?: number;
    houseName?: string;
    partitionId?: number;
    partitionName?: string;
    cycleId?: number;
    cycleName?: string;
    // System fields
    createdDate: string;
    updatedDate?: string;
    priority: TaskPriority;
    description?: string;
    done: boolean;
}

const BASE_CLASSES = 'task-sidebar elevation-4';

@Component({
    selector: 'app-task-sidebar',
    standalone: true,
    imports: [CommonModule, FormsModule, AddTaskComponent, DialogModule],
    templateUrl: './task-sidebar.component.html',
    styleUrl: './task-sidebar.component.scss'
})
export class TaskSidebarComponent implements OnInit {
    @HostBinding('class') classes: string = BASE_CLASSES;
    public ui: Observable<UiState>;
    public tasks: Task[] = [];
    public showAddTaskDialog = false;
    public statusOptions: Array<{id: number; name: string}> = [];
    public categoryOptions: Array<{id: number; name: string}> = [];
    public TaskPriority = TaskPriority;

    constructor(
        public appService: AppService,
        private store: Store<AppState>,
        private taskService: TaskService,
        private toastr: ToastrService
    ) {}

    ngOnInit() {
        this.ui = this.store.select('ui');
        this.ui.subscribe((state: UiState) => {
            this.classes = `${BASE_CLASSES} ${state.sidebarSkin}`;
        });

        this.fetchTasks();

        this.taskService.getTaskStatus().subscribe({
            next: (result: any) => {
                const list = Array.isArray(result)
                    ? result
                    : result?.data || [];
                this.statusOptions = list.map((s: any) => ({
                    id: s.id,
                    name: s.name
                }));
            },
            error: () => {
                this.statusOptions = [];
            }
        });
        this.taskService.getTaskCategories().subscribe({
            next: (result: any) => {
                const list = Array.isArray(result)
                    ? result
                    : result?.data || [];
                this.categoryOptions = list.map((c: any) => ({
                    id: c.id,
                    name: c.name
                }));
            },
            error: () => {
                this.categoryOptions = [];
            }
        });
    }

    private fetchTasks() {
        const filter: any = {};
        this.taskService.getTaskList(filter).subscribe({
            next: (res: any) => {
                const list = Array.isArray(res?.tasks)
                    ? res.tasks
                    : Array.isArray(res)
                      ? res
                      : res?.data || [];
                this.tasks = list.map((t: any) => this.mapToSidebarTask(t));
            },
            error: () => {
                this.toastr.error('Failed to load tasks');
                this.tasks = [];
            }
        });
    }

    private mapToSidebarTask(t: any): Task {
        const statusStr = (t.status || '').toString();
        const categoryStr = (t.category || '').toString();
        const assignedName = t.assignee || '';
        const farmName = t.farm || '';
        const cycleName = t.cycle || '';
        const created = t.startDate || '';
        const due = t.dueDate || '';

        return {
            id: Number(t.id) || 0,
            title: t.title || '',
            assignedTo: assignedName,
            startDate: created,
            dueDate: due,
            subTasks: Array.isArray(t.subTasks)
                ? t.subTasks.map((st: any) => ({
                      id: Number(st.id) || 0,
                      title: st.title || '',
                      createdBy: st.createdBy || 'System',
                      assignedTo: st.assignedTo || '',
                      dueDate: st.dueDate || '',
                      startDate: st.startDate || '',
                      status: st.status || 'Not Started',
                      done: st.status === 'Done'
                  }))
                : [],
            createdBy: t.createdBy || 'System',
            status: statusStr || 'Not Started',
            statusId: typeof t.statusId === 'number' ? t.statusId : undefined,
            attachments: [],
            category: categoryStr || '',
            farmId: Number(t.farmId) || 0,
            farmName,
            houseId: t.houseId ?? undefined,
            houseName: t.house || t.houseName || undefined,
            partitionId: t.partitionId ?? undefined,
            partitionName: t.partition || t.partitionName || undefined,
            cycleId: t.cycleId ?? undefined,
            cycleName,
            createdDate: created,
            updatedDate: t.updatedDate || undefined,
            priority: TaskPriority.Medium,
            description: t.description || '',
            done: statusStr === 'Done'
        } as Task;
    }

    toggleSidebar() {
        this.store.dispatch(new ToggleTaskSidebar());
    }

    markTaskAsCompleted(taskId: number, event?: Event) {
        if (event) {
            event.stopPropagation();
        }
        const task = this.tasks.find((t) => t.id === taskId);
        if (task) {
            task.done = !task.done;
        }
    }

    // Removed opening preview from sidebar; handled in tasks-list component

    onTaskUpdated(updatedTask: Task) {
        const index = this.tasks.findIndex((t) => t.id === updatedTask.id);
        if (index !== -1) {
            this.tasks[index] = updatedTask;
        }
    }

    getTasksByStatus(statusName: string) {
        const target = (statusName || '').toLowerCase();
        return this.tasks.filter(
            (task) => (task.status || '').toLowerCase() === target
        );
    }

    getTasksPriority(priority: TaskPriority) {
        return this.tasks.filter((task) => task.priority === priority);
    }

    getStatusIdByName(name?: string): number | undefined {
        if (!name) return undefined;
        const found = this.statusOptions.find(
            (s) => (s.name || '').toLowerCase() === name.toLowerCase()
        );
        return found?.id;
    }

    getStatusNameById(id?: number): string | undefined {
        if (!id) return undefined;
        return this.statusOptions.find((s) => s.id === id)?.name;
    }

    onStatusChange(task: Task, statusIdValue: number | string, event?: Event) {
        if (event) {
            event.stopPropagation();
        }
        const statusId = Number(statusIdValue);
        if (!statusId || !task?.id) return;

        const prev = {
            status: task.status,
            statusId: task.statusId,
            done: task.done
        };

        const newName = this.getStatusNameById(statusId) || task.status;
        task.status = newName;
        task.statusId = statusId;
        task.done = (newName || '').toLowerCase() === 'done';

        this.taskService
            .updateTaskStatus({taskId: task.id, statusId})
            .subscribe({
                next: () => {
                    this.toastr.success('Task status updated');
                },
                error: () => {
                    task.status = prev.status;
                    task.statusId = prev.statusId;
                    task.done = prev.done;
                    this.toastr.error('Failed to update status');
                }
            });
    }

    openAddTaskDialog() {
        this.showAddTaskDialog = true;
    }

    closeAddTaskDialog() {
        this.showAddTaskDialog = false;
    }

    onTaskSaved(newTask: any) {
        this.showAddTaskDialog = false;
        this.fetchTasks();
    }
}
